import { Play, Camera } from "lucide-react";
import VideoThumbnail from "@/components/VideoThumbnail";

// Extract game_id from raw footage URL e.g. https://assets.hify.club/full-replays/8201/130/downloadable.mp4 -> 8201
const getGameId = (url = "") => {
  const m = url.match(/\/full-replays\/(\d+)\//);
  return m ? m[1] : null;
};

// Extract play_moment_id (the segment common across angles) from clip URL
// e.g. https://dbyctxsmb6sh0.cloudfront.net/882788/100/1080p/downloadable.mp4 -> 100
const getPlayMomentId = (url = "") => {
  try {
    const path = new URL(url).pathname.split("/").filter(Boolean);
    // pattern: [<video_id>, <play_moment_id>, <quality>, <file>]
    if (path.length >= 2) return path[1];
  } catch {
    // fall-through
  }
  return null;
};

// Extract human duration (e.g. "1h 58m", "17s") from description or return null
const getDuration = (desc = "") => {
  const m = desc.match(/(\d+h\s*\d+m|\d+h|\d+m|\d+s)/);
  return m ? m[1].replace(/\s+/g, " ") : null;
};

export default function VideoCard({ video, onClick }) {
  const sportColors = {
    Pickleball: "bg-emerald-100 text-emerald-800",
    Padel: "bg-blue-100 text-blue-800",
    Cricket: "bg-amber-100 text-amber-800",
    Football: "bg-red-100 text-red-800",
    Badminton: "bg-purple-100 text-purple-800",
  };

  const thumbSrc = video.thumbnail_url || video.angles?.[0]?.url || "";
  const firstUrl = video.angles?.[0]?.url || "";
  const isRaw = video.video_type === "raw_footage";

  const idLabel = isRaw
    ? (video.game_id || getGameId(firstUrl))
    : (video.play_moment_id || getPlayMomentId(firstUrl));
  const title = isRaw
    ? `Game: #${idLabel ?? "—"}`
    : `Clip: #${idLabel ?? "—"}`;

  const duration = video.duration || getDuration(video.description || "");

  return (
    <div
      className="bg-white rounded-xl border border-[#0A1128]/10 overflow-hidden hover:shadow-[0_8px_30px_rgba(10,17,40,0.06)] hover:-translate-y-1 transition-all duration-300 cursor-pointer video-card-hover"
      onClick={() => onClick(video)}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden">
        <VideoThumbnail
          src={thumbSrc}
          alt={title}
          sport={video.sport}
          className="w-full h-full"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition-colors">
          <div className="w-14 h-14 rounded-full bg-[#FF5A00]/90 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <Play className="w-6 h-6 text-white fill-white ml-1" />
          </div>
        </div>
        {video.angles && video.angles.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
            <Camera className="w-3 h-3" />
            {video.angles.length} angles
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3
          className="text-sm font-semibold text-[#0A1128] truncate leading-tight mb-2"
          style={{ fontFamily: "Outfit" }}
          data-testid={`video-title-${video.id}`}
        >
          {title}
        </h3>

        {/* Single-line subtitle */}
        <div
          className="flex items-center gap-2 text-xs text-[#0A1128]/60 truncate"
          data-testid={`video-subtitle-${video.id}`}
        >
          {duration && (
            <span className="font-mono tabular-nums">{duration}</span>
          )}
          {duration && <span className="text-[#0A1128]/25">·</span>}
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              sportColors[video.sport] || "bg-gray-100 text-gray-800"
            }`}
          >
            {video.sport}
          </span>
          {!isRaw && video.sporting_moment && (
            <>
              <span className="text-[#0A1128]/25">·</span>
              <span className="italic text-[#FF5A00] font-medium tracking-wide truncate">
                {video.sporting_moment}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
